import React, { useState, useEffect } from 'react';
import {
  Megaphone,
  AlertTriangle,
  AlertCircle,
  Sparkles,
  ExternalLink,
  X,
  Check,
  Calendar,
  UserCheck
} from 'lucide-react';
import { markPopupDismissed, shouldShowPopupForUser } from './popupService';

/**
 * Kullanıcıların ekranında (Mobil ve Masaüstü) anında beliren Pop-up Bildirim Modalı
 */
export default function ActivePopupModal({
  user,
  popups = [],
  db,
  isFirebaseActive
}) {
  const [activePopup, setActivePopup] = useState(null);
  const [isDismissing, setIsDismissing] = useState(false);

  // Gösterilecek pop-up'ı belirle
  useEffect(() => {
    if (!user || !popups || popups.length === 0) {
      setActivePopup(null);
      return;
    }

    // Kullanıcının henüz kapatmadığı ve kendisine hitap eden ilk aktif pop-up'ı bul
    const matching = popups.find(p => shouldShowPopupForUser(p, user));
    setActivePopup(matching || null);
  }, [user, popups]);

  if (!activePopup) return null;

  // Pop-up türüne göre tema renkleri ve ikonlar
  const typeConfig = {
    urgent: {
      badgeText: '🚨 ACİL DUYURU',
      badgeBg: 'bg-rose-500/15 border-rose-500/30 text-rose-400',
      icon: AlertCircle,
      iconBg: 'bg-rose-500/20 text-rose-400 ring-4 ring-rose-500/10',
      gradient: 'from-rose-600/20 via-slate-900 to-slate-900',
      borderColor: 'border-rose-500/40',
      btnBg: 'bg-rose-600 hover:bg-rose-500 shadow-rose-900/30'
    },
    warning: {
      badgeText: '⚠️ ÖNEMLİ BİLDİRİM',
      badgeBg: 'bg-amber-500/15 border-amber-500/30 text-amber-400',
      icon: AlertTriangle,
      iconBg: 'bg-amber-500/20 text-amber-400 ring-4 ring-amber-500/10',
      gradient: 'from-amber-600/20 via-slate-900 to-slate-900',
      borderColor: 'border-amber-500/40',
      btnBg: 'bg-amber-600 hover:bg-amber-500 shadow-amber-900/30'
    },
    success: {
      badgeText: '🎉 DUYURU & TEBRİK',
      badgeBg: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400',
      icon: Sparkles,
      iconBg: 'bg-emerald-500/20 text-emerald-400 ring-4 ring-emerald-500/10',
      gradient: 'from-emerald-600/20 via-slate-900 to-slate-900',
      borderColor: 'border-emerald-500/40',
      btnBg: 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-900/30'
    },
    info: {
      badgeText: '📢 SİSTEM BİLDİRİMİ',
      badgeBg: 'bg-sky-500/15 border-sky-500/30 text-sky-400',
      icon: Megaphone,
      iconBg: 'bg-sky-500/20 text-sky-400 ring-4 ring-sky-500/10',
      gradient: 'from-sky-600/20 via-slate-900 to-slate-900',
      borderColor: 'border-sky-500/40',
      btnBg: 'bg-sky-600 hover:bg-sky-500 shadow-sky-900/30'
    }
  };

  const currentType = typeConfig[activePopup.type] || typeConfig.info;
  const IconComponent = currentType.icon;

  const handleDismiss = async () => {
    if (isDismissing) return;
    setIsDismissing(true);

    try {
      await markPopupDismissed(activePopup.id, user.id, db, isFirebaseActive);
    } catch (e) {
      console.error("Popup dismiss hatası:", e);
    }

    // Kısa geçiş animasyonu
    setTimeout(() => {
      // Bir sonraki bekleyen pop-up varsa ona geçiş yapacak
      const nextMatching = popups.find(p => p.id !== activePopup.id && shouldShowPopupForUser(p, user));
      setActivePopup(nextMatching || null);
      setIsDismissing(false);
    }, 200);
  };

  const handleLinkClick = () => {
    if (!activePopup.actionLink) return;
    try {
      window.open(activePopup.actionLink, '_blank', 'noopener,noreferrer');
    } catch (e) {
      window.location.href = activePopup.actionLink;
    }
  };

  // Tarih formatı
  let formattedDate = '';
  if (activePopup.createdAt) {
    try {
      const d = new Date(activePopup.createdAt);
      formattedDate = d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
    } catch (e) {}
  }

  return (
    <div className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-black/75 backdrop-blur-md transition-opacity duration-300 ${isDismissing ? 'opacity-0' : 'opacity-100 animate-fade-in'}`}>
      
      {/* Pop-up Kartı (Mobil ve Masaüstü Uyumlu) */}
      <div 
        className={`relative w-full max-w-lg bg-gradient-to-b ${currentType.gradient} bg-slate-900 border ${currentType.borderColor} rounded-3xl shadow-2xl overflow-hidden transform transition-all duration-300 scale-100 max-h-[92vh] flex flex-col`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="popup-title"
      >
        
        {/* Dekoratif Işık Efekti */}
        <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-72 h-72 bg-white/5 rounded-full blur-3xl pointer-events-none" />

        {/* Üst Kapatma Butonu (Hafif) */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-2 rounded-full bg-slate-800/60 hover:bg-slate-700/80 text-slate-400 hover:text-white transition-colors z-20"
          title="Kapat"
        >
          <X size={18} />
        </button>

        {/* Gövde Alanı - Kaydırılabilir */}
        <div className="p-6 sm:p-8 overflow-y-auto custom-scrollbar flex-1">
          
          {/* İkon & Rozet */}
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${currentType.iconBg}`}>
              <IconComponent size={24} />
            </div>
            <div>
              <span className={`inline-block text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${currentType.badgeBg}`}>
                {currentType.badgeText}
              </span>
              {formattedDate && (
                <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                  <Calendar size={11} /> {formattedDate}
                </div>
              )}
            </div>
          </div>

          {/* Başlık */}
          <h2 id="popup-title" className="text-xl sm:text-2xl font-black text-white tracking-tight mb-3 leading-snug">
            {activePopup.title}
          </h2>

          {/* Mesaj İçeriği */}
          <div className="text-sm sm:text-base text-slate-200 leading-relaxed whitespace-pre-wrap bg-slate-800/40 p-4 rounded-2xl border border-slate-700/50 mb-4 selection:bg-orange-500 selection:text-white">
            {activePopup.message}
          </div>

          {/* Gönderen Bilgisi */}
          {activePopup.authorName && (
            <div className="flex items-center gap-2 text-xs text-slate-400 px-1 mb-2">
              <UserCheck size={13} className="text-slate-500" />
              <span>Gönderen: <b className="text-slate-300 font-medium">{activePopup.authorName}</b></span>
            </div>
          )}
        </div>

        {/* Alt Butonlar Alanı */}
        <div className="p-4 sm:p-6 bg-slate-900/90 border-t border-slate-800/80 flex flex-col sm:flex-row items-center gap-3">
          
          {/* Opsiyonel Link Butonu */}
          {activePopup.actionLink && (
            <button
              onClick={handleLinkClick}
              className="w-full sm:w-auto flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-slate-700 hover:border-slate-500 text-slate-300 hover:text-white bg-slate-800/70 hover:bg-slate-700/70 text-sm font-semibold transition-all"
            >
              <ExternalLink size={16} />
              <span>{activePopup.actionLinkText || 'Bağlantıyı Aç'}</span>
            </button>
          )}

          {/* Ana Onay / Kapat Butonu */}
          <button
            onClick={handleDismiss}
            disabled={isDismissing}
            className={`w-full ${activePopup.actionLink ? 'sm:w-auto sm:flex-1' : 'w-full'} flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl text-white font-bold text-sm tracking-wide shadow-xl transition-all active:scale-[0.98] ${currentType.btnBg}`}
          >
            <Check size={18} />
            <span>{activePopup.buttonText || 'Tamam, Anladım'}</span>
          </button>
        </div>

      </div>
    </div>
  );
}
